import { Button, ButtonVariant, Container, ContainerHeader, Table } from '@Core/Components'
import { Accent, PageHeading } from '@/components/brand/brand'
import { Loader } from '@Core/Components/Loader/Loader'
import { PermissionWrapper } from '@Core/Components/PermissionWrapper/PermissionWrapper'
import { usePurchasingSuppliers } from '@Purchasing/Hooks/usePurchasingSuppliers/usePurchasingSuppliers'
import { PurchasingSupplier } from '@Purchasing/Domain/Model/PurchaseProcess'
import { roles } from '@Purchasing/roles'
import { formatLeadTime } from '@Purchasing/Utils/money'
import { PurchasingSupplierFormModal } from './PurchasingSupplierFormModal'
import { ChangeEvent, useState } from 'react'

export const PurchasingSupplierList = () => {
    const {
        suppliers,
        loading,
        error,
        reload,
        search,
        createSupplier,
        updateSupplier,
    } = usePurchasingSuppliers()
    const [formOpen, setFormOpen] = useState(false)
    const [editing, setEditing] = useState<PurchasingSupplier | null>(null)
    const [submitting, setSubmitting] = useState(false)

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        search(e.target.value)
    }

    const handleSubmit = async (values: any) => {
        setSubmitting(true)
        let result
        if (editing?._id) {
            result = await updateSupplier(editing._id, values)
        } else {
            result = await createSupplier({ ...values, active: true })
        }
        setSubmitting(false)
        if (result !== null && result !== undefined) {
            await reload()
            return result
        }
        return null
    }

    const handleToggleActive = async (supplier: PurchasingSupplier) => {
        await updateSupplier(supplier._id, { active: !supplier.active })
        await reload()
    }

    return (
        <Container>
            <ContainerHeader>
                <PageHeading eyebrow="Compras">
                    <Accent>Fornecedores</Accent> de compras
                </PageHeading>
                <div className="flex items-center gap-3">
                    <input
                        type="text"
                        placeholder="Buscar por nome/CNPJ..."
                        onChange={handleSearch}
                        className="border rounded px-3 py-1.5 text-sm"
                    />
                    <PermissionWrapper
                        permission={roles.supplier.permissions.create.key}
                    >
                        <Button
                            variant={ButtonVariant.primary}
                            onClick={() => {
                                setEditing(null)
                                setFormOpen(true)
                            }}
                        >
                            Novo Fornecedor
                        </Button>
                    </PermissionWrapper>
                </div>
            </ContainerHeader>

            {error && <p className="text-od-secondary-600 text-sm">{error}</p>}

            {loading ? (
                <Loader />
            ) : (
                <Table data-testid="purchasing-supplier-table">
                    <thead>
                        <tr>
                            <th>Razão Social</th>
                            <th>Nome Fantasia</th>
                            <th>CNPJ</th>
                            <th>Contato</th>
                            <th>Telefone</th>
                            <th>E-mail</th>
                            <th>Pagamento</th>
                            <th>Prazo</th>
                            <th>Status</th>
                            <th>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!loading && suppliers.length === 0 && (
                            <tr>
                                <td colSpan={10}>
                                    Nenhum fornecedor encontrado
                                </td>
                            </tr>
                        )}
                        {!loading &&
                            suppliers.map((supplier) => (
                                <tr key={supplier._id} data-testid={`supplier-row-${supplier._id}`}>
                                    <td>{supplier.corporateName}</td>
                                    <td>{supplier.tradeName || '-'}</td>
                                    <td>{supplier.cnpj || '-'}</td>
                                    <td>{supplier.contactName || '-'}</td>
                                    <td>{supplier.phone || '-'}</td>
                                    <td>{supplier.mail || '-'}</td>
                                    <td>{supplier.paymentTerms || '-'}</td>
                                    <td>
                                        {formatLeadTime(
                                            supplier.defaultLeadTimeDays
                                        )}
                                    </td>
                                    <td>
                                        {supplier.active === false
                                            ? 'Inativo'
                                            : 'Ativo'}
                                    </td>
                                    <td className="flex gap-1 justify-center">
                                        <PermissionWrapper
                                            permission={
                                                roles.supplier.permissions
                                                    .update.key
                                            }
                                        >
                                            <Button
                                                variant={ButtonVariant.secondary}
                                                onClick={() => {
                                                    setEditing(supplier)
                                                    setFormOpen(true)
                                                }}
                                            >
                                                Editar
                                            </Button>
                                            <Button
                                                variant={ButtonVariant.secondary}
                                                onClick={() =>
                                                    handleToggleActive(supplier)
                                                }
                                            >
                                                {supplier.active === false
                                                    ? 'Ativar'
                                                    : 'Inativar'}
                                            </Button>
                                        </PermissionWrapper>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </Table>
            )}

            <PurchasingSupplierFormModal
                isOpen={formOpen}
                onClose={() => {
                    setFormOpen(false)
                    setEditing(null)
                }}
                supplier={editing}
                onSubmit={handleSubmit}
                submitting={submitting}
            />
        </Container>
    )
}